/**
 * Sharing a listing.
 *
 * Three destinations and no third-party JavaScript: the network buttons are
 * ordinary links to public composer addresses, and the copy button uses the
 * clipboard the browser already has. Nothing here loads a script from another
 * origin, so pasting a listing into a conversation does not hand a tracker the
 * fact that somebody read it.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { CheckIcon } from './ui';

/** How long the button stays confirmed after a copy. */
const CONFIRMED_MS = 2000;

export interface ShareRowProps {
  readonly title: string;
  readonly companyName: string;
  /** Absolute address of the listing. */
  readonly url: string;
}

function CopyIcon(): ReactNode {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect
        x="5.4"
        y="5.4"
        width="8.1"
        height="8.1"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <path
        d="M10.6 3.4A1.9 1.9 0 0 0 8.9 2.5H4.4a1.9 1.9 0 0 0-1.9 1.9v4.5c0 .74.42 1.38 1.03 1.7"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function XIcon(): ReactNode {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M1.4 1.4h3.2l4 5.3 4.2-5.3h1.8l-5.1 6.4 5.4 7.1h-3.2l-4.2-5.6-4.4 5.6H1.3l5.4-6.8L1.4 1.4Z"
        fill="currentColor"
      />
    </svg>
  );
}

function LinkedInIcon(): ReactNode {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M3.6 5.9h2.1v7.5H3.6V5.9Zm1.05-3.3a1.22 1.22 0 1 1 0 2.44 1.22 1.22 0 0 1 0-2.44ZM7.3 5.9h2v1.02h.03c.28-.53 1-1.09 2.05-1.09 2.19 0 2.6 1.44 2.6 3.32v4.25h-2.1V9.57c0-.81-.02-1.85-1.13-1.85-1.13 0-1.3.88-1.3 1.79v3.89H7.3V5.9Z"
        fill="currentColor"
      />
    </svg>
  );
}

export function ShareRow({ title, companyName, url }: ShareRowProps): ReactNode {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = useCallback(() => {
    const confirm = (): void => {
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), CONFIRMED_MS);
    };

    // An insecure origin, an older browser, or a refused permission all land
    // in the same place: the address goes into a hidden field and is copied
    // the way it was copied before there was an API for it.
    const fallback = (): void => {
      const field = document.createElement('textarea');
      field.value = url;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.opacity = '0';
      document.body.appendChild(field);
      field.select();
      try {
        document.execCommand('copy');
        confirm();
      } catch {
        // Nothing left to try. The address is still in the browser's own bar,
        // which is where a reader looks next.
      } finally {
        document.body.removeChild(field);
      }
    };

    const clipboard = navigator.clipboard as Clipboard | undefined;
    if (clipboard === undefined) {
      fallback();
      return;
    }
    void clipboard.writeText(url).then(confirm, fallback);
  }, [url]);

  const summary = `${title} at ${companyName}`;
  const x = `https://x.com/intent/post?text=${encodeURIComponent(summary)}&url=${encodeURIComponent(url)}`;
  const linkedin = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;

  return (
    <section className="card is-padded sharecard">
      <span className="eyebrow">Share this listing</span>
      <div className="sharerow">
        <button type="button" className="pill pill-quiet pill-sm" onClick={copy}>
          {copied ? <CheckIcon /> : <CopyIcon />}
          <span aria-live="polite">{copied ? 'Link copied' : 'Copy link'}</span>
        </button>
        <a
          className="pill pill-quiet pill-sm"
          href={x}
          target="_blank"
          rel="noreferrer noopener"
          aria-label={`Share ${summary} on X`}
        >
          <XIcon />X
        </a>
        <a
          className="pill pill-quiet pill-sm"
          href={linkedin}
          target="_blank"
          rel="noreferrer noopener"
          aria-label={`Share ${summary} on LinkedIn`}
        >
          <LinkedInIcon />
          LinkedIn
        </a>
      </div>
    </section>
  );
}
